// The whole account as the web knows it (the kit's StoreSnapshot): the records of every collection the web reads,
// the person's own document, and what sharing adds (the shares they are in, invitations waiting for them, and their
// placements in shares of others). Records of a share of someone else carry this person's own values already
// (sharing.ts: `placedRecord`).

import type {
  Area, Birthday, Favorite, Habit, InboxTrace, Placement, Project, RecordTypes, Section, Share, Situation, Template, Todo,
  UserDocument,
} from "../core/records";
import type { Edits, RecordEdits } from "../core/edits";
import type { Invitation } from "./invitations";

export interface Account {
  todos: Todo[];
  projects: Project[];
  areas: Area[];
  sections: Section[];
  templates: Template[];
  /** Habits (docs/63): their days are to-dos with `habitID`. */
  habits: Habit[];
  birthdays: Birthday[];
  situations: Situation[];
  inboxTraces: InboxTrace[];
  /** Absent until the person's document said (or when it holds a list the web can't read: then nothing is written). */
  favorites?: Favorite[];
  user: UserDocument;
  shares: Share[];
  invitations: Invitation[];
  /** This person's placements in shares of others, by share id. */
  placements: Record<string, Placement>;
}

/** The record collections the web reads and writes, under `users/{uid}` (and the first four in shares). */
export const ACCOUNT_COLLECTIONS = [
  "todos", "projects", "areas", "sections", "templates", "habits", "birthdays", "situations", "inboxTraces",
] as const satisfies readonly (keyof RecordTypes)[];

export type AccountCollection = (typeof ACCOUNT_COLLECTIONS)[number];

/**
 * The collections the web listens to. Inbox traces are only written (the Inbox's history is an iPhone screen); the
 * web keeps those it wrote itself, so Undo can take them back.
 */
export const LISTENED_COLLECTIONS: readonly AccountCollection[] = [
  "todos", "projects", "areas", "sections", "templates", "habits", "birthdays", "situations",
];

export function emptyAccount(): Account {
  return {
    todos: [], projects: [], areas: [], sections: [], templates: [], habits: [], birthdays: [], situations: [], inboxTraces: [],
    user: { datesConverted: false, unknownFields: {} }, shares: [], invitations: [], placements: {},
  };
}

/** What one change saves and deletes, per collection, and the favorites when they changed (StoreChanges). */
export type Changes = { [Name in AccountCollection]?: RecordEdits<RecordTypes[Name]> } & { favorites?: Favorite[] };

/** The core's edits as changes (the marks travel apart). */
export function changesOf(edits: Edits): Changes {
  return { todos: edits.todos, projects: edits.projects, sections: edits.sections, areas: edits.areas, inboxTraces: edits.inboxTraces };
}

export function isNoChange(changes: Changes): boolean {
  return changes.favorites === undefined
    && ACCOUNT_COLLECTIONS.every((name) => (changes[name]?.saved.length ?? 0) === 0 && (changes[name]?.deleted.length ?? 0) === 0);
}

/** The ids a change touches, per collection. */
export function touchedIDs(changes: Changes): { [Name in AccountCollection]?: Set<string> } {
  const result: { [Name in AccountCollection]?: Set<string> } = {};
  for (const name of ACCOUNT_COLLECTIONS) {
    const edits = changes[name];
    if (!edits || (edits.saved.length === 0 && edits.deleted.length === 0)) continue;
    result[name] = new Set([...edits.saved.map((record) => record.id), ...edits.deleted]);
  }
  return result;
}

/**
 * The records with a change applied: a saved record replaces the one of its id where it is, or comes last; a deleted
 * one goes. Unchanged collections keep their arrays, and an empty change returns the account itself.
 */
export function applyChanges(account: Account, changes: Changes): Account {
  let result: Account | undefined;
  for (const name of ACCOUNT_COLLECTIONS) {
    const edits = changes[name] as RecordEdits<{ id: string }> | undefined;
    if (!edits || (edits.saved.length === 0 && edits.deleted.length === 0)) continue;
    result ??= { ...account };
    (result as unknown as Record<string, unknown>)[name] = applied(account[name], edits);
  }
  if (changes.favorites !== undefined) {
    result ??= { ...account };
    result.favorites = changes.favorites;
  }
  return result ?? account;
}

function applied<T extends { id: string }>(records: readonly T[], edits: RecordEdits<T>): T[] {
  const saved = new Map(edits.saved.map((record) => [record.id, record]));
  const deleted = new Set(edits.deleted);
  const result: T[] = [];
  for (const record of records) {
    if (deleted.has(record.id)) continue;
    const replacement = saved.get(record.id);
    if (replacement) {
      result.push(replacement);
      saved.delete(record.id);
    } else {
      result.push(record);
    }
  }
  for (const record of saved.values()) if (!deleted.has(record.id)) result.push(record);
  return result;
}

/** Records by id; the first of an id wins, as in the kit. */
export function byID<T extends { id: string }>(records: readonly T[] | undefined): Map<string, T> {
  const map = new Map<string, T>();
  for (const record of records ?? []) if (!map.has(record.id)) map.set(record.id, record);
  return map;
}
